import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Furnitures.css";

interface FurnitureItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Furnitures: React.FC = () => {
  const [data, setData] = useState<FurnitureItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/furniture`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching furniture:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: FurnitureItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: FurnitureItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Furniture",
    de: "Möbel",
    ta: "தளபாடங்கள்",
    si: "ගෘහ භාණ්ඩ",
    hi: "फर्नीचर",
    ar: "أثاث",
    tr: "Mobilya",
    ru: "Мебель",
    zh: "家具",
    fr: "Meubles",
    es: "Muebles",
  };

  const subtitleMap = {
    en: "Furniture in German",
    de: "Möbel auf Deutsch",
    ta: "ஜெர்மன் மொழியில் தளபாடங்கள்",
    si: "ජර්මන් භාෂාවෙන් ගෘහ භාණ්ඩ",
    hi: "जर्मन में फर्नीचर",
    ar: "الأثاث بالألمانية",
    tr: "Almanca'da Mobilya",
    ru: "Мебель на немецком",
    zh: "德语家具",
    fr: "Meubles en allemand",
    es: "Muebles en alemán",
  };

  return (
    <div className="furnitures-page-container">
      <div className="furnitures-page-header">
        <div>
          <h1 className="furnitures-page-title">{titleMap["de"]}</h1>
          <p className="furnitures-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="furnitures-page-loading">Loading...</div>
      ) : (
        <div className="furnitures-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;

            return (
              <div
                key={item.id}
                className={`furnitures-page-card ${isPlaying ? "furnitures-page-playing" : ""}`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="furnitures-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="furnitures-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="furnitures-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="furnitures-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="furnitures-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Furnitures;
